import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneIcon, VideoIcon, MessageIcon, ContactsIcon, ArrowDownLeftIcon, ArrowUpRightIcon } from "@icons";
import type { Call } from "@aria/shared";
import { useAuth } from "@hooks/useAuth";
import { useVoice } from "@lib/voiceContext";
import { useDialer } from "@lib/dialerContext";
import { apiClient } from "@lib/apiClient";
import { fmtNumber, toE164 } from "@lib/phone";
import styles from "./HomePage.module.css";

const AVATARS: { bg: string; fg: string }[] = [
  { bg: "#dbe9fb", fg: "#0066cc" },
  { bg: "#e4e4ea", fg: "#636366" },
  { bg: "#d6f2dd", fg: "#34c759" },
  { bg: "#ffe9cc", fg: "#ff9500" },
  { bg: "#f4ecff", fg: "#7a3ff2" },
];
function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function initialsOf(s: string): string {
  const letters = s.replace(/[^A-Za-z]/g, "");
  if (letters.length >= 2) return letters.slice(0, 2).toUpperCase();
  const digits = s.replace(/\D/g, "");
  if (digits.length >= 2) return digits.slice(-2);
  return (s.trim()[0] ?? "?").toUpperCase();
}

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

function fmtTalk(totalSec: number): string {
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.round(totalSec / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase();
}

export function HomePage() {
  const { user } = useAuth();
  const voice = useVoice();
  const { openDialer } = useDialer();
  const navigate = useNavigate();
  const isSuper = user?.role === "SUPERADMIN";
  const [calls, setCalls] = useState<Call[]>([]);

  useEffect(() => {
    apiClient.get<Call[]>("/history/calls?limit=40").then(setCalls).catch(() => {});
  }, []);

  const firstName = (user?.displayName ?? user?.email ?? "").split(/[\s@]/)[0] || "there";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const today = new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
  const initials = (user?.displayName ?? user?.email ?? "?")
    .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";

  const stats = useMemo(() => {
    const todays = calls.filter((c) => isToday(c.startedAt));
    const talk = todays.reduce((s, c) => s + (c.durationSec ?? 0), 0);
    const missed = todays.filter((c) => ["NO_ANSWER", "BUSY", "FAILED", "CANCELED"].includes(c.status)).length;
    return [
      { val: String(todays.length), label: "Calls today" },
      { val: fmtTalk(talk), label: "Talk time" },
      { val: String(missed), label: "Missed" },
    ];
  }, [calls]);

  const recent = calls.slice(0, 3);
  const busy = voice.status === "in-call" || voice.status === "connecting";
  const party = (c: Call) => (c.direction === "INBOUND" ? c.from : c.to);
  const callBack = (to: string) => {
    const num = toE164(to, Boolean(isSuper));
    if (num) void voice.dial(num);
  };

  const quick = [
    { label: "New call", icon: <PhoneIcon width={21} height={21} />, tint: "green", onClick: openDialer },
    { label: "Room", icon: <VideoIcon width={21} height={21} />, tint: "blue", onClick: () => navigate("/dashboard/video") },
    { label: "Message", icon: <MessageIcon width={21} height={21} />, tint: "purple", onClick: () => navigate("/dashboard/messages") },
    { label: "Contacts", icon: <ContactsIcon width={21} height={21} />, tint: "orange", onClick: () => navigate("/dashboard/contacts") },
  ];

  return (
    <div className={styles.page}>
      <header className={styles.hero}>
        <span className={styles.blobA} aria-hidden />
        <span className={styles.blobB} aria-hidden />
        <div className={styles.heroTop}>
          <div className={styles.heroText}>
            <div className={styles.date}>{today}</div>
            <h1 className={styles.greeting}>{greeting}, {firstName}</h1>
          </div>
          <div className={styles.avatar} aria-hidden>
            {initials}
            <span className={styles.presence} />
          </div>
        </div>
        <div className={styles.stats}>
          {stats.map((s) => (
            <div key={s.label} className={styles.stat}>
              <div className={styles.statVal}>{s.val}</div>
              <div className={styles.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>
      </header>

      <div className={styles.body}>
        <div className={styles.quickGrid}>
          {quick.map((q) => (
            <button key={q.label} className={styles.quick} onClick={q.onClick}>
              <span className={styles.quickIcon} data-tint={q.tint}>{q.icon}</span>
              <span className={styles.quickLabel}>{q.label}</span>
            </button>
          ))}
        </div>

        <div className={styles.sectionRow}>
          <span className={styles.sectionTitle}>Recent</span>
          <button className={styles.seeAll} onClick={() => navigate("/dashboard/calls")}>See all</button>
        </div>
        <div className={styles.card}>
          {recent.length === 0 ? (
            <div className={styles.empty}>No calls yet — tap the green button to place one.</div>
          ) : (
            recent.map((c) => {
              const p = party(c);
              const missed = ["NO_ANSWER", "BUSY", "FAILED", "CANCELED"].includes(c.status);
              const av = AVATARS[hash(p) % AVATARS.length] ?? { bg: "#e4e4ea", fg: "#636366" };
              return (
                <div key={c.id} className={styles.row}>
                  <div className={styles.rowAvatar} style={{ background: av.bg, color: av.fg }}>{initialsOf(p)}</div>
                  <div className={styles.rowBody}>
                    <div className={styles.rowName} style={missed ? { color: "var(--color-red)" } : undefined}>
                      {fmtNumber(p)}
                    </div>
                    <div className={styles.rowMeta} style={missed ? { color: "var(--color-red)" } : undefined}>
                      {c.direction === "INBOUND" ? <ArrowDownLeftIcon width={14} height={14} /> : <ArrowUpRightIcon width={14} height={14} />}
                      <span>{missed ? "Missed" : c.direction === "INBOUND" ? "Incoming" : "Outgoing"} · {fmtTime(c.startedAt)}</span>
                    </div>
                  </div>
                  <button className={styles.rowCall} disabled={busy} onClick={() => callBack(p)} aria-label={`Call ${fmtNumber(p)}`}>
                    <PhoneIcon width={17} height={17} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
